import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { STATUS_META } from "@/pages/partner/PartnerDashboard";
import { Loader2, Search, UserPlus, ArrowRight } from "lucide-react";

const FILTERS = ["all", "invited", "active", "in_treatment", "fulfilled", "cancelled"];

export default function PartnerReferrals() {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/partner/referrals"); setRefs(data.referrals || []); }
      catch { /* gate handles */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return refs.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!term) return true;
      return [r.member_first_name, r.member_last_name, r.member_email, r.pet_name].some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [refs, q, filter]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-7 h-7 text-paw-primary/40 animate-spin" /></div>;

  return (
    <div className="max-w-[1080px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-paw-navy">Referrals</h1>
          <p className="mt-1 text-paw-text-secondary">{refs.length} total</p>
        </div>
        <Link to="/partner/submit" className="inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-6 py-3 font-semibold text-sm transition-all"><UserPlus className="w-4 h-4" /> Submit a referral</Link>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, or pet" className="w-full pl-10 pr-4 py-2.5 rounded-full border border-paw-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3.5 py-2 rounded-full text-xs font-semibold capitalize transition ${filter === f ? "bg-paw-navy text-white" : "bg-white border border-paw-border text-paw-text-secondary hover:border-paw-text-muted"}`}>
              {f === "all" ? "All" : (STATUS_META[f]?.label || f)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 bg-white border border-paw-border rounded-3xl shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-paw-text-secondary">No referrals match.</p>
            <Link to="/partner/submit" className="mt-4 inline-flex items-center gap-2 text-paw-primary font-semibold text-sm">Submit a referral <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="divide-y divide-paw-border">
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-paw-text-muted bg-paw-cream/50">
              <div className="col-span-4">Member</div><div className="col-span-3">Pet</div><div className="col-span-3">Submitted</div><div className="col-span-2 text-right">Status</div>
            </div>
            {filtered.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.submitted;
              const d = r.created_at ? new Date(r.created_at).toLocaleDateString() : "";
              return (
                <div key={r.referral_id} className="px-6 py-3.5 grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-3 sm:items-center">
                  <div className="sm:col-span-4 min-w-0">
                    <p className="font-semibold text-paw-navy truncate">{r.member_first_name} {r.member_last_name}</p>
                    <p className="text-sm text-paw-text-muted truncate">{r.member_email}</p>
                  </div>
                  <div className="sm:col-span-3 text-sm text-paw-text-secondary truncate">{r.pet_name ? `${r.pet_name}${r.pet_type ? ` · ${r.pet_type}` : ""}` : "n/a"}</div>
                  <div className="sm:col-span-3 text-sm text-paw-text-secondary">{d}</div>
                  <div className="sm:col-span-2 sm:text-right"><span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span></div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
